// USAGE: StepsLayout - Reusable Controlled Stepper
// -------------------------------------------
// This component implements a controlled stepper pattern where step state is managed here,
// and the ProgressStepper is a controlled component that receives currentStep and onStepChange props.
//
// To add a new step:
// 1. Create a React component (e.g., const MyStep: React.FC = () => <div>...</div>)
// 2. Add it to the `steps` array below with the required properties
//
// Each step object supports these attributes:
// - name: string (required) - The label shown in the stepper UI
// - component: React.ComponentType<any> (required) - The React component to render for this step
// - optional: boolean (optional) - If true, marks the step as optional in the UI
// - showButton: boolean (optional) - If true, shows the bottom action bar (e.g., Save button) for this step
// - handleSave: () => void (optional) - Function called when the Save button is clicked for this step
// - saveButtonContent: React.ReactNode (optional) - Custom content for the Save button for this step
// - disabled: boolean (optional) - If true, disables this step and all following steps (cascading)
//
// Step Validation & Navigation:
// - The stepper automatically integrates with Formik validation
// - currentStep state controls which step is active
// - onStepChange handler manages step navigation
//
// Example:
// const steps: ProgressStepperStep[] = [
//   { name: 'Step 1', component: Step1Component },
//   { name: 'Step 2', component: Step2Component, optional: true, showButton: true },
//   { name: 'Step 3', component: Step3Component, disabled: true }
// ];
//
// The stepper renders each step in order and handles navigation, validation, disabling, and optional logic automatically.
// See the ProgressStepperStep interface for full typing.

import React, { useState, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormikContext } from 'formik';
import _ from 'lodash';

import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import { Context } from 'components/Store';
import { CROP_SEASONS } from 'reducers/reducer';

import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import {
  FormValues,
  CropZone,
  initialDetailsValues,
  initialProductsValues,
  initialPropertiesValues,
  initialWeatherValues
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import {
  GO_BACK,
  useAgOperationUtility
} from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import { paths } from 'routes/paths';
import LoadingSpinner from 'components/LoadingSpinner';
import editShowDiscardConfirmModal from './Helpers/editDiscardConfirmModal';
import ProgressStepper, {
  ProgressStepperStep
} from './Components/ProgressStepper';
import showDiscardConfirmModal from './Helpers/discardConfirmModal';
import showCropSeasonConfirmModal from './Helpers/cropSeasonConfirmModal';
import showNoCropZonesModal from './Helpers/noCropZonesModal';
import DetailsStep from './Components/DetailsStep';
import PropertiesStep from './Components/PropertiesStep';
import ProductsStep from './Components/ProductsStep';
import WeatherStep from './Components/WeatherStep';

import './index.css';

export enum TypeLayout {
  APPLICATION = 'application',
  WORK_ORDER = 'workOrder'
}

interface StepsLayoutProps {
  isEditMode?: boolean;
  isLoading?: boolean;
  hasChanges?: boolean;
  type?: TypeLayout;
}

const LoadingFirstStep: React.FC = () => (
  <div className="w-full h-full flex items-center justify-center">
    <LoadingSpinner className="h-19 w-60 py-8 px-4 rounded-lg bg-white shadow-md" />
  </div>
);

const StepsLayout: React.FC<StepsLayoutProps> = ({
  isEditMode = false,
  isLoading = false,
  hasChanges,
  type = TypeLayout.APPLICATION
}) => {
  const history = useHistory();
  const [state, dispatch] = useContext(Context) as any;
  const { cropSeasons } = state;
  const {
    handleSaveApplication,
    handleSaveWorkOrder,
    isSavingForm,
    updateUtilityState,
    operationType,
    setOperationType,
    utilityState
  } = useAgOperationUtility();
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const previousSeasonsRef = useRef<string[]>([]);

  useEffect(() => {
    updateUtilityState({ isEditMode });
  }, [isEditMode, updateUtilityState]);

  useEffect(() => {
    setOperationType(type as any);
  }, [type, setOperationType]);

  const handleSave = (stepName?: string) => {
    if (type === TypeLayout.WORK_ORDER) {
      handleSaveWorkOrder(stepName);
      return;
    }
    handleSaveApplication(stepName);
  };

  // Step-specific save handlers that pass the step name
  const handleSaveFromDetails = () =>
    handleSave(LayoutStrings.stepNames[type].details);
  const handleSaveFromProperties = () =>
    handleSave(LayoutStrings.stepNames.selectProperties);
  const handleSaveFromProducts = () =>
    handleSave(LayoutStrings.stepNames.selectProducts);
  const handleSaveFromWeather = () =>
    handleSave(LayoutStrings.stepNames.weather);

  const [currentStep, setCurrentStep] = useState(0);

  const isApplication = type === TypeLayout.APPLICATION;
  const saveButtonContent: React.ReactNode = (
    <span className="w-full text-center">
      {LayoutStrings.buttons[type].saveButton}
    </span>
  );

  const steps: ProgressStepperStep[] = [
    {
      name: LayoutStrings.stepNames[type].details,
      component: isLoading || !operationType ? LoadingFirstStep : DetailsStep,
      initialValues: initialDetailsValues,
      ...(isEditMode
        ? {
            showButton: true,
            handleSave: handleSaveFromDetails,
            saveButtonContent
          }
        : {})
    },
    {
      name: LayoutStrings.stepNames.selectProperties,
      component: PropertiesStep,
      initialValues: initialPropertiesValues,
      ...(isEditMode
        ? {
            showButton: true,
            handleSave: handleSaveFromProperties,
            saveButtonContent
          }
        : {})
    },
    {
      name: LayoutStrings.stepNames.selectProducts,
      component: ProductsStep,
      initialValues: initialProductsValues,
      prerequisites: [
        'cropZones',
        'totalArea',
        'totalAppliedAcreage'
      ] as (keyof FormValues)[],
      showButton: true,
      handleSave: handleSaveFromProducts,
      saveButtonContent
    },
    ...(isApplication
      ? [
          {
            name: LayoutStrings.stepNames.weather,
            component: WeatherStep,
            initialValues: initialWeatherValues,
            prerequisites: [
              'startDateTime',
              'endDateTime',
              'cropZones'
            ] as (keyof FormValues)[],
            optional: true,
            showButton: true,
            handleSave: handleSaveFromWeather,
            saveButtonContent
          }
        ]
      : [])
  ];

  // Track whether we're allowing navigation (after user confirms discard)
  const allowNavigationRef = useRef(false);
  // Skip the initial navigation that mounts the component
  const hasMountedRef = useRef(false);

  // Block route navigation when in create/edit mode
  useEffect(() => {
    // Defer blocking until after the component has mounted
    // so we don't intercept the navigation that brought us here
    requestAnimationFrame(() => {
      hasMountedRef.current = true;
    });
  }, []);

  useEffect(() => {
    const unblock = history.block(location => {
      // Allow navigation if user already confirmed discard or form is saving
      if (
        !hasMountedRef.current ||
        allowNavigationRef.current ||
        isSavingForm
      ) {
        return undefined; // allow navigation
      }

      // In edit mode, only block if there are unsaved changes
      if (isEditMode && !hasChanges) {
        return undefined;
      }

      const navigateAway = () => {
        allowNavigationRef.current = true;
        history.push(location.pathname + location.search);
      };

      if (isEditMode) {
        editShowDiscardConfirmModal(navigateAway);
      } else {
        showDiscardConfirmModal(navigateAway, operationType);
      }

      return false; // block navigation
    });

    return () => {
      unblock();
    };
  }, [history, isEditMode, hasChanges, isSavingForm, operationType]);

  // Warn on browser tab close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSavingForm) return;
      if (isEditMode && !hasChanges) return;
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEditMode, hasChanges, isSavingForm]);

  const handleStepChange = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  const handleCancel = () => {
    const moveToLandingPage = () => {
      allowNavigationRef.current = true;
      // If a returnPath is specified (e.g., from work order to app creation), use it
      if (utilityState?.returnPath) {
        if (utilityState.returnPath === GO_BACK) {
          history.goBack();
          return;
        }
        history.push(utilityState.returnPath);
        return;
      }
      // Otherwise use the default landing page based on type
      if (type === TypeLayout.WORK_ORDER) {
        history.push(paths.workOrders);
        return;
      }
      history.push(paths.applications);
    };
    if (isEditMode) {
      if (hasChanges) {
        editShowDiscardConfirmModal(moveToLandingPage);
      } else {
        moveToLandingPage();
      }
      return;
    }
    showDiscardConfirmModal(moveToLandingPage, operationType);
  };

  const handleCropSeasonValidation = (seasonIds: string[]) => {
    // Capture current localStorage seasons BEFORE applySelections() overwrites them
    previousSeasonsRef.current = JSON.parse(
      localStorage.getItem('selectedCropSeasons') ?? '[]'
    );

    // Check if all selected seasons have NO crop zones
    const selectedSeasons =
      cropSeasons?.filter((season: any) => seasonIds.includes(season.id)) || [];
    const hasNoSelectableCropZones =
      selectedSeasons.length > 0 &&
      selectedSeasons.every(
        (season: any) => !season.cropZoneIds || season.cropZoneIds.length === 0
      );

    // If all selected seasons have NO crop zones, show warning and return false
    if (hasNoSelectableCropZones) {
      showNoCropZonesModal({ isApplication });
      return false; // Validation failed - breadcrumb won't save
    }

    return true; // Validation passed - breadcrumb can proceed
  };

  const handleCropSeasonConfirm = async (
    seasonIds: string[],
    selectFunction: () => Promise<void>
  ) => {
    // Get preliminary list of crop zones in newly selected crop seasons
    const filteredCropZones = _.filter(values.cropZones, (zone: CropZone) =>
      _.some(zone.cropSeasons, (season: { id: string }) =>
        _.includes(seasonIds, season.id)
      )
    );

    // Determines whether the crop season change actually triggered a change in selected crop zones
    const willCropZonesChange =
      values.cropZones.length !== filteredCropZones.length;

    const onOk = async () => {
      await selectFunction();
      const selectedCropSeasons = JSON.parse(
        localStorage.getItem('selectedCropSeasons') ?? '[]'
      );
      updateUtilityState({ selectedCropSeasons });
      setFieldValue('cropZones', filteredCropZones);
      if (currentStep === 2 && willCropZonesChange) {
        handleStepChange(1);
      }
    };

    // If no crop zones will be lost on crop season change, then select them directly
    if (!willCropZonesChange) {
      await onOk();
      return;
    }

    // If crop zones will be lost on crop season change, then show confirmation modal
    const onCancel = () => {
      // Revert localStorage to the previous crop seasons
      localStorage.setItem(
        'selectedCropSeasons',
        JSON.stringify(previousSeasonsRef.current)
      );
      // Dispatch with a new reference to trigger breadcrumb re-read from localStorage
      dispatch({ type: CROP_SEASONS, payload: [...cropSeasons] });
    };

    showCropSeasonConfirmModal({ onOk, onCancel });
  };

  return (
    <div className="h-full w-full relative">
      <Breadcrumb
        hideOrganizationDropdown={false}
        hideCropSeasonDropdown={false}
        onCropSeasonConfirm={handleCropSeasonConfirm}
        onCropSeasonValidation={handleCropSeasonValidation}
        disabledCropSeasonCreate
        allowMultipleCropSeasonSelection
        disabled
      >
        <Breadcrumb.Item
          title={LayoutStrings.breadcrumb[type].label}
          value={
            isEditMode
              ? LayoutStrings.breadcrumb[type].editStep
              : LayoutStrings.breadcrumb[type].createStep
          }
          isLast
        />
      </Breadcrumb>
      <ProgressStepper
        stepperName={
          isEditMode
            ? LayoutStrings.stepper[type].editTitle
            : LayoutStrings.stepper[type].createTitle
        }
        steps={steps}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        disableSaveButton={isSavingForm}
        disableStepButtons={isSavingForm || isLoading}
        onCancel={handleCancel}
      />
      {isSavingForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 9999 }}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-8"
            style={{ width: '424px' }}
          >
            <LoadingSpinner
              message={
                type === TypeLayout.WORK_ORDER
                  ? 'Saving Work Order...'
                  : 'Saving Application...'
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * NOTE: No need to wrap this file in a new Formik context.
 * The Formik context is already provided by the RouteAdmin component.
 * To access form state, values, or helpers, use Formik hooks (e.g., useFormikContext) directly in your step components or children.
 * Do NOT wrap additional Formik providers here—this ensures a single, consistent form state tree.
 */

export default StepsLayout;
